import { useMutation, useQueryClient, type UseMutationResult } from '@tanstack/react-query';
import { queryKeys } from '../../lib/queryKeys';
import { boardKeys, logTime, timeKeys, type LogTimeInput, type TimeEntry } from './api';

/**
 * Log one time entry against a request, from wherever the form is mounted —
 * the Board card popover, or the request screen (once RequestDetailPage wires
 * in `RequestTimeLog.tsx` — see this feature's report to the lead).
 *
 * Not run through `useOptimisticMutation`: logging is additive, not a
 * reorder — there is no existing row on screen to move, so there is nothing
 * for an optimistic patch to do beyond append a not-yet-real id to a list the
 * server is about to hand back anyway. A plain invalidate-on-success is the
 * honest version of "we don't know the row until the server assigns it".
 *
 * Three caches move on a successful log, because a minute logged is visible
 * in three places at once: the entries list on the request/card, the card's
 * own `loggedHours` total on the Board, and (per requestActivities.kind
 * `request.time_logged`) the Activity tab — invalidating that key is a no-op
 * if nothing has that query mounted, so it costs nothing when Activity is
 * not on screen.
 */
export function useLogTime(
  workspaceId: string,
  requestId: string,
): UseMutationResult<TimeEntry, Error, LogTimeInput> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: LogTimeInput) => logTime(workspaceId, requestId, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: timeKeys.forRequest(workspaceId, requestId) });
      void queryClient.invalidateQueries({ queryKey: boardKeys.list(workspaceId) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.requestActivity(workspaceId, requestId) });
    },
  });
}
